import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  CheckCircle2,
  Plus,
  AlertCircle,
  Sparkles,
  ChevronDown,
  ChevronUp,
  FileText,
  X as XIcon,
  TrendingUp,
  Library,
  History,
  Building2,
  ArrowRight,
  Search,
  BarChart3,
  BookOpen,
  Goal
} from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import ImprovedObjectiveWizard from '@/components/performance/objective-setting/ImprovedObjectiveWizard'
import { InfoTooltip } from '@/components/ui/InfoTooltip'
import { getPillarColor, getFunctionalAreaColor } from './types'
import CopyObjectivesPanel from './CopyObjectivesPanel'

interface AllocationTabProps {
  teamMember: { display_name: string; employee_id?: string } | null
  /** The performance year objectives are added for (HR UAT list #27). */
  year: number
  /** After objectives were copied in from the year before or the Objective Library. */
  onObjectivesCopied: () => Promise<void>
  allocationSource: 'scratch' | 'template' | 'prior_year' | 'strategic' | null
  setAllocationSource: (source: 'scratch' | 'template' | 'prior_year' | 'strategic' | null) => void
  showObjectiveWizard: boolean
  setShowObjectiveWizard: (show: boolean) => void
  availableTemplates: any[]
  selectedTemplate: any
  setSelectedTemplate: (template: any) => void
  expandedTemplateObjective: number | null
  setExpandedTemplateObjective: (id: number | null) => void
  allocatingTemplate: boolean
  onAllocateTemplate: () => void
  onAllocateObjectives: (objectives: any[]) => void
}

export default function AllocationTab({
  teamMember,
  year,
  onObjectivesCopied,
  allocationSource,
  setAllocationSource,
  showObjectiveWizard,
  setShowObjectiveWizard,
  availableTemplates,
  selectedTemplate,
  setSelectedTemplate,
  expandedTemplateObjective,
  setExpandedTemplateObjective,
  allocatingTemplate,
  onAllocateTemplate,
  onAllocateObjectives
}: AllocationTabProps) {
  const [templateSearch, setTemplateSearch] = useState('')
  const [templateTypeFilter, setTemplateTypeFilter] = useState('all')

  if (!allocationSource) {
    return (
      <>
        <p className="text-sm text-muted-foreground mb-1">
          Choose how to add objectives for <span className="font-semibold text-foreground">{year}</span>
        </p>
        <div id="tour-objective-options" className="grid grid-cols-2 gap-4" data-tour="objective-options">
          {/* Option 1: From Scratch */}
          <Card
            className="border-2 hover:border-heritage-blue cursor-pointer transition-all hover:shadow-md"
            onClick={() => setAllocationSource('scratch')}
          >
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Plus className="h-5 w-5 text-heritage-blue" />
                From Scratch
                <InfoTooltip text={<ul className="space-y-1"><li><strong>S</strong>pecific — Clear and well-defined</li><li><strong>M</strong>easurable — Quantifiable outcomes</li><li><strong>A</strong>chievable — Realistic and attainable</li><li><strong>R</strong>elevant — Aligned with strategic goals</li><li><strong>T</strong>ime-bound — Clear deadlines</li></ul>} />
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                Create custom objectives with KPIs, timelines, and SMART analysis
              </p>
            </CardContent>
          </Card>

          {/* Option 2: From Template Library */}
          <Card
            className="border-2 hover:border-future-blue cursor-pointer transition-all hover:shadow-md"
            onClick={() => setAllocationSource('template')}
          >
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Library className="h-5 w-5 text-future-blue" />
                From Template
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                Pre-approved templates aligned with roles
              </p>
            </CardContent>
          </Card>

          {/* Option 3: From Prior Year */}
          <Card
            className="border-2 hover:border-amber-500 cursor-pointer transition-all hover:shadow-md"
            onClick={() => setAllocationSource('prior_year')}
          >
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <History className="h-5 w-5 text-amber-600" />
                From Prior Year
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                Bring in objectives from {year - 1}, with their KPIs
              </p>
            </CardContent>
          </Card>

          {/* Option 4: From Objective Library */}
          <Card
            className="border-2 hover:border-emerald-500 cursor-pointer transition-all hover:shadow-md"
            onClick={() => setAllocationSource('strategic')}
          >
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Building2 className="h-5 w-5 text-emerald-600" />
                From Library
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                Objectives HR has put in the Objective Library
              </p>
            </CardContent>
          </Card>
        </div>
      </>
    )
  }

  if (allocationSource === 'scratch' && !showObjectiveWizard) {
    return (
      <div className="space-y-4">
        <Button
          variant="outline"
          onClick={() => setAllocationSource(null)}
          className="mb-4"
        >
          <ChevronUp className="h-4 w-4 mr-2 rotate-180" />
          Back
        </Button>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5 text-heritage-blue" />
              New Objective
            </CardTitle>
            <CardDescription>
              Use the wizard to build a tailored objective for {teamMember?.display_name}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-semibold text-sm text-blue-900 mb-2">Our AI-empowered "Objective Wizard" will assist in optimising:</h4>
              <ul className="space-y-2 text-sm text-blue-800">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <span className="flex items-center">
                    SMART criteria
                    <InfoTooltip text={<ul className="space-y-1"><li><strong>S</strong>pecific — Clear and well-defined</li><li><strong>M</strong>easurable — Quantifiable outcomes</li><li><strong>A</strong>chievable — Realistic and attainable</li><li><strong>R</strong>elevant — Aligned with strategic goals</li><li><strong>T</strong>ime-bound — Clear deadlines</li></ul>} />
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <span className="flex items-center">
                    KPI targets and timelines
                    <InfoTooltip text="Key Performance Indicators — measurable values that demonstrate how effectively objectives are being achieved." />
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <span className="flex items-center">
                    Strategic objective and KPA alignment
                    <InfoTooltip text="Eduvos strategic objectives (Impact, Culture, Quality, Scale) and Key Performance Areas." />
                  </span>
                </li>
              </ul>
            </div>

            <Button
              onClick={() => setShowObjectiveWizard(true)}
              className="w-full bg-heritage-blue hover:bg-heritage-blue/90"
              size="lg"
            >
              <Sparkles className="h-4 w-4 mr-2" />
              Start Creating Objective
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (allocationSource === 'scratch' && showObjectiveWizard) {
    return (
      <div className="space-y-4">
        <Button
          variant="outline"
          onClick={() => {
            setAllocationSource(null)
            setShowObjectiveWizard(false)
          }}
          className="mb-4"
        >
          <ChevronUp className="h-4 w-4 mr-2 rotate-180" />
          Back
        </Button>
        <ImprovedObjectiveWizard
          year={year}
          onSave={onAllocateObjectives}
          onCancel={() => {
            setAllocationSource(null)
            setShowObjectiveWizard(false)
          }}
        />
      </div>
    )
  }

  if (allocationSource === 'template') {
    const query = templateSearch.trim().toLowerCase()
    const filteredTemplates = availableTemplates.filter((template) => {
      const matchesSearch = !query ||
        template.name?.toLowerCase().includes(query) ||
        template.description?.toLowerCase().includes(query)
      const matchesType = templateTypeFilter === 'all' || template.template_type === templateTypeFilter
      return matchesSearch && matchesType
    })

    return (
      <div className="space-y-4">
        <Button
          variant="outline"
          onClick={() => {
            setAllocationSource(null)
            setSelectedTemplate(null)
          }}
          className="mb-4"
        >
          <ChevronUp className="h-4 w-4 mr-2 rotate-180" />
          Back
        </Button>

        {!selectedTemplate ? (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Library className="h-5 w-5" />
                Templates
              </CardTitle>
              <CardDescription>
                Select a template for {teamMember?.display_name}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search templates..."
                    value={templateSearch}
                    onChange={(e) => setTemplateSearch(e.target.value)}
                    className="pl-9"
                  />
                </div>
                <Select value={templateTypeFilter} onValueChange={setTemplateTypeFilter}>
                  <SelectTrigger className="w-48">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="role_based">Role-Based</SelectItem>
                    <SelectItem value="institutional">Institutional</SelectItem>
                    <SelectItem value="generic">Generic</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {availableTemplates.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Library className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p className="font-medium">No templates available</p>
                  <p className="text-sm mt-2">Check back later for approved templates</p>
                </div>
              ) : filteredTemplates.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Search className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p className="font-medium">No templates match your search</p>
                  <p className="text-sm mt-2">Try a different search term or type filter</p>
                </div>
              ) : (
                <div className="grid gap-3">
                  {filteredTemplates.map((template) => (
                    <Card
                      key={template.id}
                      className="border-2 hover:border-future-blue cursor-pointer transition-all hover:shadow-md"
                      onClick={() => setSelectedTemplate(template)}
                    >
                      <CardHeader className="pb-3">
                        <div className="flex justify-between items-start">
                          <div>
                            <CardTitle className="text-base flex items-center gap-2">
                              {template.name}
                            </CardTitle>
                            <CardDescription className="mt-1">
                              {template.description}
                            </CardDescription>
                          </div>
                          <Badge variant="secondary">
                            {template.template_type}
                          </Badge>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Goal className="h-4 w-4" />
                            {template.objectives_data?.objectives?.length || 0} objectives
                          </span>
                          <span className="flex items-center gap-1">
                            <TrendingUp className="h-4 w-4" />
                            Used {template.usage_count || 0} times
                          </span>
                          <span className="ml-auto flex items-center gap-1 text-future-blue font-medium">
                            Select Template
                            <ArrowRight className="h-4 w-4" />
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Library className="h-5 w-5" />
                    {selectedTemplate.name}
                  </CardTitle>
                  <CardDescription>
                    Review and customise before adding
                  </CardDescription>
                </div>
                <Badge>{selectedTemplate.template_type}</Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-900">
                  {selectedTemplate.description}
                </p>
              </div>

              <div className="space-y-3">
                <h4 className="font-semibold flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Included Objectives ({selectedTemplate.objectives_data?.objectives?.length || 0})
                </h4>

                <AnimatePresence mode="popLayout">
                  {selectedTemplate.objectives_data?.objectives?.map((obj: any, idx: number) => {
                    const kpaColor = obj.kpa ? getFunctionalAreaColor(obj.kpa.name) : 'bg-gray-100 text-gray-800'
                    const pillarColor = obj.pillar ? getPillarColor(obj.pillar.name) : 'bg-gray-100 text-gray-800'

                    return (
                      <motion.div
                        key={obj.id || idx}
                        layout
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, x: -100 }}
                      >
                        <Card className="overflow-hidden border-l-4 border-l-blue-500">
                          <CardHeader
                            className="pb-3 cursor-pointer hover:bg-gray-50 transition-colors"
                            onClick={() => setExpandedTemplateObjective(
                              expandedTemplateObjective === obj.id ? null : obj.id
                            )}
                          >
                            <div className="flex items-start justify-between gap-4">
                              <div className="flex-1 space-y-2">
                                <div className="flex items-center gap-2">
                                  <Goal className="h-4 w-4 text-blue-600" />
                                  <CardTitle className="text-base">{obj.title}</CardTitle>
                                </div>

                                <div className="flex flex-wrap gap-2">
                                  {obj.kpa && (
                                    <Badge className={kpaColor}>
                                      <BookOpen className="h-3 w-3 mr-1" />
                                      {obj.kpa.display_name}
                                    </Badge>
                                  )}
                                  {obj.pillar && (
                                    <Badge className={pillarColor}>
                                      {obj.pillar.name}
                                    </Badge>
                                  )}
                                  <Badge variant="outline" className="gap-1">
                                    <BarChart3 className="h-3 w-3" />
                                    {obj.kpis?.length || 0} KPIs
                                  </Badge>
                                </div>
                              </div>

                              <div className="flex-shrink-0">
                                {expandedTemplateObjective === obj.id ? (
                                  <ChevronUp className="h-5 w-5 text-gray-500" />
                                ) : (
                                  <ChevronDown className="h-5 w-5 text-gray-500" />
                                )}
                              </div>
                            </div>
                          </CardHeader>

                          <AnimatePresence>
                            {expandedTemplateObjective === obj.id && (
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.2 }}
                              >
                                <CardContent className="space-y-4 border-t pt-4">
                                  <div>
                                    <Label className="text-sm font-semibold mb-2 block">Description</Label>
                                    <p className="text-sm text-muted-foreground">{obj.description}</p>
                                  </div>

                                  {(obj.start_date || obj.end_date) && (
                                    <div className="flex gap-4">
                                      {obj.start_date && (
                                        <div className="flex-1">
                                          <Label className="text-xs text-muted-foreground">Start Date</Label>
                                          <p className="text-sm">{new Date(obj.start_date).toLocaleDateString()}</p>
                                        </div>
                                      )}
                                      {obj.end_date && (
                                        <div className="flex-1">
                                          <Label className="text-xs text-muted-foreground">End Date</Label>
                                          <p className="text-sm">{new Date(obj.end_date).toLocaleDateString()}</p>
                                        </div>
                                      )}
                                    </div>
                                  )}

                                  {obj.kpis && obj.kpis.length > 0 && (
                                    <div>
                                      <Label className="text-sm font-semibold mb-3 block flex items-center gap-2">
                                        <BarChart3 className="h-4 w-4" />
                                        Key Performance Indicators ({obj.kpis.length})
                                      </Label>
                                      <div className="space-y-3">
                                        {obj.kpis.map((kpi: any, kpiIdx: number) => (
                                          <div key={kpi.id || kpiIdx} className="bg-gray-50 rounded-lg p-3 space-y-2">
                                            <div className="flex items-start justify-between">
                                              <div className="flex-1">
                                                <p className="font-medium text-sm">{kpi.name}</p>
                                                {kpi.description && (
                                                  <p className="text-xs text-muted-foreground mt-1">{kpi.description}</p>
                                                )}
                                              </div>
                                              {kpi.status && (
                                                <Badge variant="outline" className="ml-2">
                                                  {kpi.status}
                                                </Badge>
                                              )}
                                            </div>
                                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3 text-xs">
                                              <div>
                                                <span className="text-muted-foreground">Target:</span>
                                                <span className="ml-1 font-medium">{kpi.target_value || kpi.target || '-'} {kpi.unit || ''}</span>
                                              </div>
                                              <div>
                                                <span className="text-muted-foreground">Frequency:</span>
                                                <span className="ml-1 font-medium capitalize">{kpi.frequency || '-'}</span>
                                              </div>
                                              {kpi.weight && (
                                                <div>
                                                  <span className="text-muted-foreground">Weight:</span>
                                                  <span className="ml-1 font-medium">{kpi.weight}%</span>
                                                </div>
                                              )}
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </CardContent>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </Card>
                      </motion.div>
                    )
                  })}
                </AnimatePresence>
              </div>

              <div className="flex gap-2 pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => setSelectedTemplate(null)}
                  className="flex-1"
                >
                  <XIcon className="h-4 w-4 mr-2" />
                  Cancel
                </Button>
                <Button
                  onClick={onAllocateTemplate}
                  className="flex-1"
                  disabled={!selectedTemplate?.objectives_data?.objectives?.length || allocatingTemplate}
                >
                  {allocatingTemplate ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                      Adding...
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="h-4 w-4 mr-2" />
                      Add {selectedTemplate?.objectives_data?.objectives?.length || 0} Objective{(selectedTemplate?.objectives_data?.objectives?.length || 0) === 1 ? '' : 's'}
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    )
  }

  if ((allocationSource === 'prior_year' || allocationSource === 'strategic') && teamMember?.employee_id) {
    return (
      <CopyObjectivesPanel
        source={allocationSource === 'prior_year' ? 'prior_year' : 'library'}
        employeeId={teamMember.employee_id}
        memberName={teamMember.display_name}
        year={year}
        onBack={() => setAllocationSource(null)}
        onAdded={onObjectivesCopied}
      />
    )
  }

  return null
}
